import React from 'react'
import {
  Box,
  TextField,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material'
import { useLocale } from 'src/locales/useLocale'
import { MetaLines } from './MetaLines'
import { numericValidationError } from './validation'

type Props = {
  content: string
  contentMaxLength: number
  embroideryIndex?: number
  caution?: string
  description?: string
  disabled?: boolean
  allowOnlyNumeric?: boolean
  handleContent: (selected: string, i?: number) => void
}

export const EmbroideryContentMobile: React.FC<Props> = ({
  content,
  contentMaxLength,
  embroideryIndex,
  caution,
  description,
  disabled,
  allowOnlyNumeric = false,
  handleContent
}) => {
  const { t } = useLocale()
  const [isDialogOpen, setIsDialogOpen] = React.useState(false)
  const [draftContent, setDraftContent] = React.useState(content)
  const [error, setError] = React.useState('')

  React.useEffect(() => {
    if (!isDialogOpen) {
      setDraftContent(content)
      setError('')
    }
  }, [content, isDialogOpen])

  const heading = t.EMBROIDERY_CONTENTS
  const dialogTitle =
    embroideryIndex !== undefined
      ? t.EMBROIDERY_CONTENT_DIALOG_TITLE.replace('{n}', String(embroideryIndex + 1)).replace('{summary}', heading)
      : heading

  const openDialog = () => {
    if (disabled) return
    setDraftContent(content)
    setError('')
    setIsDialogOpen(true)
  }

  const handleListFieldFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    event.target.blur()
    openDialog()
  }

  const handleDraftChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setDraftContent(value)
    setError(numericValidationError(value, allowOnlyNumeric))
  }

  const handleCancel = () => {
    setDraftContent(content)
    setError('')
    setIsDialogOpen(false)
  }

  const handleComplete = () => {
    const trimmed = draftContent.trim()
    const validationError = numericValidationError(trimmed, allowOnlyNumeric)
    if (validationError) {
      setError(validationError)
      return
    }
    handleContent(trimmed, embroideryIndex)
    setIsDialogOpen(false)
  }

  return (
    <>
      <Card>
        <Box fontSize={14} textAlign={'left'} pl={2} pb={1} fontWeight={'bold'}>
          {heading}
        </Box>
        <Box display="flex" alignItems="center" mx={1}>
          <TextField
            maxRows={1}
            variant="outlined"
            style={{ width: '100%', background: disabled ? 'gainsboro' : '#fff' }}
            value={content}
            placeholder={t.ENTER_EMBROIDERY_CONTENTS}
            inputProps={{ maxLength: contentMaxLength, readOnly: true }}
            disabled={disabled}
            onClick={openDialog}
            onFocus={handleListFieldFocus}
          />
        </Box>
        <MetaLines contentMaxLength={contentMaxLength} caution={caution} description={description} />
      </Card>

      <Dialog
        open={isDialogOpen}
        onClose={handleCancel}
        fullWidth
        scroll="body"
        sx={{ '& .MuiDialog-container': { alignItems: 'flex-end' } }}
        PaperProps={{
          sx: {
            margin: 0,
            width: '100%',
            maxWidth: '100%',
            borderRadius: '16px 16px 0 0'
          }
        }}
        BackdropProps={{
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.35)' }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', fontSize: 16 }}>{dialogTitle}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            variant="outlined"
            value={draftContent}
            onChange={handleDraftChange}
            placeholder={t.ENTER_EMBROIDERY_CONTENTS}
            inputProps={{ maxLength: contentMaxLength }}
            error={!!error}
            helperText={error}
          />
          <MetaLines contentMaxLength={contentMaxLength} caution={caution} description={description} />
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, justifyContent: 'space-between' }}>
          <Button variant="outlined" onClick={handleCancel}>
            {t.SELECT_CARD_WITH_CONFIRM_CANCEL}
          </Button>
          <Button variant="contained" color="primary" onClick={handleComplete} disabled={!!error}>
            {t.EMBROIDERY_CONTENT_INPUT_COMPLETE}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
